import type { FeedResource } from '../resources/feed.js';
import type { FeedCache } from '../cache/feed-cache.js';
import type {
  FeedEngageOp,
  FeedPost,
  FeedThread,
  LiveFeedChangeHandler,
  LiveFeedNoticeHandler,
  PublishFeedPostRequest,
} from '../types/feed.js';

export const LIVE_FEED_PAGE_SIZE = 50;
export const LIVE_FEED_WINDOW_PAGES = 3;
export const LIVE_FEED_WINDOW_MAX = LIVE_FEED_PAGE_SIZE * LIVE_FEED_WINDOW_PAGES;
export const LIVE_FEED_THREAD_REPLIES = 20;

type Page = FeedPost[];

/**
 * Stateful Live Feed client — peer of ChatSession. Owns the home window
 * (at most three pages / 150 posts), apply, and publish (feed ADR-008).
 */
export class LiveFeed {
  private pages: Page[] = [];
  private changeHandlers = new Set<LiveFeedChangeHandler>();
  private noticeHandlers = new Set<LiveFeedNoticeHandler>();
  private _unseenCount = 0;
  private _openThread: FeedThread | null = null;

  constructor(
    private readonly resource: FeedResource,
    private readonly feedCache: FeedCache,
  ) {}

  get homeItems(): FeedPost[] {
    return this.pages.flat();
  }

  get unseenCount(): number {
    return this._unseenCount;
  }

  get openThread(): FeedThread | null {
    return this._openThread;
  }

  /** Initial home page. Called by FeedResource.connect. */
  async hydrate(): Promise<void> {
    const page = await this.resource.listHome({ limit: LIVE_FEED_PAGE_SIZE });
    this.pages = page.posts.length > 0 ? [page.posts] : [];
    this._unseenCount = page.unseen_count;
    this.emitChange();
  }

  /**
   * Fetch the next older page. Drops the newest buffered page when the
   * window would exceed three pages.
   */
  async loadOlder(): Promise<FeedPost[]> {
    const oldest = this.oldestPost();
    const page = await this.resource.listHome({
      limit: LIVE_FEED_PAGE_SIZE,
      ...(oldest
        ? { after: { created_at: oldest.created_at, id: oldest.id } }
        : {}),
    });
    if (page.posts.length === 0) {
      return [];
    }
    this.pages = [...this.pages, page.posts];
    this.trimFromNewest();
    this.emitChange();
    return page.posts;
  }

  /**
   * Fetch newer posts than the current window. Restores a page dropped by
   * loadOlder.
   */
  async loadNewer(): Promise<FeedPost[]> {
    const newest = this.newestPost();
    const page = await this.resource.listHome({
      limit: LIVE_FEED_PAGE_SIZE,
      ...(newest
        ? { before: { created_at: newest.created_at, id: newest.id } }
        : {}),
    });
    if (page.posts.length === 0) {
      return [];
    }
    this.pages = [page.posts, ...this.pages];
    this.trimFromOldest();
    this.emitChange();
    return page.posts;
  }

  /** Publish through the resource and apply the returned post into the window. */
  async publish(params: PublishFeedPostRequest): Promise<FeedPost> {
    const post = await this.resource.publish(params);
    this.apply(post, { notice: false, trustViewer: true });
    return post;
  }

  async engage(postId: string, op: FeedEngageOp): Promise<FeedPost> {
    const post = await this.resource.engage(postId, { op });
    this.apply(post, { notice: false, trustViewer: true });
    return post;
  }

  async markSeen(): Promise<number> {
    const out = await this.resource.markSeen();
    this._unseenCount = out.unseen_count;
    this.emitChange();
    return this._unseenCount;
  }

  async loadThread(postId: string): Promise<FeedThread> {
    this._openThread = await this.resource.getThread(postId);
    this.emitChange();
    return this._openThread;
  }

  async delete(postId: string): Promise<FeedPost> {
    const post = await this.resource.delete(postId);
    this.apply(post, { notice: false, trustViewer: true });
    return post;
  }

  /**
   * Upsert a GET-shaped post into the home window (dedup by post id).
   * Incoming fanout snapshots keep local viewer_* unless trustViewer.
   */
  apply(post: FeedPost, opts?: { notice?: boolean; trustViewer?: boolean }): void {
    // A feed event (or the owner's own apply — publish/engage/delete) landed:
    // every cached home window refetches on its next read.
    this.feedCache.evictAll();
    const threadChanged = this.applyToOpenThread(post, opts);
    if (post.kind === 'reply') {
      if (opts?.notice) {
        this.emitNotice(post);
      }
      if (opts?.notice || threadChanged) {
        this.emitChange();
      }
      return;
    }
    const existing = this.homeItems.find((p) => p.id === post.id);
    const merged = existing && !opts?.trustViewer
      ? {
          ...post,
          viewer_liked: existing.viewer_liked,
          viewer_bookmarked: existing.viewer_bookmarked,
          viewer_reposted: existing.viewer_reposted,
        }
      : post;
    if (existing) {
      this.pages = this.pages.map((page) =>
        page.map((p) => (p.id === post.id ? merged : p)),
      );
      this.emitChange();
      return;
    }
    const all = this.homeItems;
    const newest = all[0];
    const oldest = all[all.length - 1];
    const isNewest = !newest || postOrder(merged, newest) < 0;
    if (oldest && postOrder(merged, oldest) > 0 && all.length >= LIVE_FEED_PAGE_SIZE) {
      return;
    }
    if (this.pages.length === 0) {
      this.pages = [[merged]];
    } else if (isNewest) {
      this.pages = [[merged, ...this.pages[0]], ...this.pages.slice(1)];
    } else {
      this.pages = insertInOrder(this.pages, merged);
    }
    this.trimFromOldest();
    this.emitChange();
    if (opts?.notice && isNewest) {
      this._unseenCount += 1;
      this.emitNotice(merged);
    }
  }

  /** Subscribe to home-window changes. */
  onChange(handler: LiveFeedChangeHandler): () => void {
    this.changeHandlers.add(handler);
    return () => {
      this.changeHandlers.delete(handler);
    };
  }

  /** Subscribe to toast-worthy notices (new posts / replies-to-viewer). */
  onNotice(handler: LiveFeedNoticeHandler): () => void {
    this.noticeHandlers.add(handler);
    return () => {
      this.noticeHandlers.delete(handler);
    };
  }

  private applyToOpenThread(
    post: FeedPost,
    opts?: { trustViewer?: boolean },
  ): boolean {
    const thread = this._openThread;
    if (!thread) {
      return false;
    }
    const merge = (current: FeedPost): FeedPost => {
      if (!opts?.trustViewer) {
        return {
          ...post,
          viewer_liked: current.viewer_liked,
          viewer_bookmarked: current.viewer_bookmarked,
          viewer_reposted: current.viewer_reposted,
        };
      }
      return post;
    };
    let changed = false;
    const patch = (current: FeedPost): FeedPost => {
      if (current.id !== post.id) {
        return current;
      }
      changed = true;
      return merge(current);
    };
    const root = thread.root ? patch(thread.root) : thread.root;
    const focused = patch(thread.focused);
    const ancestors = thread.ancestors.map(patch);
    let replies = thread.replies.map(patch);
    let truncated = thread.truncated;
    if (
      post.kind === 'reply' &&
      !this.threadContains(thread, post.id) &&
      this.replyBelongsToThread(thread, post)
    ) {
      replies = [...replies, post];
      if (replies.length > LIVE_FEED_THREAD_REPLIES) {
        replies = replies.slice(-LIVE_FEED_THREAD_REPLIES);
        truncated = true;
      }
      changed = true;
    }
    if (!changed) {
      return false;
    }
    this._openThread = { root, ancestors, focused, replies, truncated };
    return true;
  }

  private threadContains(thread: FeedThread, postId: string): boolean {
    if (thread.root?.id === postId || thread.focused.id === postId) {
      return true;
    }
    return (
      thread.ancestors.some((p) => p.id === postId) ||
      thread.replies.some((p) => p.id === postId)
    );
  }

  private replyBelongsToThread(thread: FeedThread, post: FeedPost): boolean {
    const rootId = thread.root?.id ?? thread.focused.root_id ?? thread.focused.id;
    if (post.root_id === rootId) {
      return true;
    }
    return post.parent_id != null && this.threadContains(thread, post.parent_id);
  }

  private oldestPost(): FeedPost | undefined {
    const last = this.pages[this.pages.length - 1];
    return last?.[last.length - 1];
  }

  private newestPost(): FeedPost | undefined {
    return this.pages[0]?.[0];
  }

  private trimFromNewest(): void {
    while (this.heldCount() > LIVE_FEED_WINDOW_MAX && this.pages.length > 1) {
      this.pages = this.pages.slice(1);
    }
  }

  private trimFromOldest(): void {
    while (this.heldCount() > LIVE_FEED_WINDOW_MAX && this.pages.length > 1) {
      this.pages = this.pages.slice(0, -1);
    }
  }

  private heldCount(): number {
    return this.pages.reduce((n, page) => n + page.length, 0);
  }

  private emitChange(): void {
    for (const handler of this.changeHandlers) {
      handler();
    }
  }

  private emitNotice(post: FeedPost): void {
    for (const handler of this.noticeHandlers) {
      handler(post);
    }
  }
}

/** Home order: newest first, `created_at` then `id` descending, the server's keyset order. */
function postOrder(a: FeedPost, b: FeedPost): number {
  if (a.created_at !== b.created_at) return a.created_at > b.created_at ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id > b.id ? -1 : 1;
}

function insertInOrder(pages: FeedPost[][], post: FeedPost): FeedPost[][] {
  const next = pages.map((page) => [...page]);
  for (const page of next) {
    const index = page.findIndex((p) => postOrder(post, p) < 0);
    if (index >= 0) {
      page.splice(index, 0, post);
      return next;
    }
  }
  next[next.length - 1].push(post);
  return next;
}
